/**
 * Tiny fetch wrapper. Always includes credentials (cookies) since auth is
 * cookie-based. Same-origin only — never call cross-origin.
 *
 * Throws ApiError on non-2xx so callers can react in Tanstack Query's
 * onError handlers without parsing manually.
 */
export class ApiError extends Error {
  status: number;
  detail?: string | undefined;
  code?: string | undefined;
  constructor(message: string, status: number, detail?: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (detail !== undefined) this.detail = detail;
    if (code !== undefined) this.code = code;
  }
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(path, init);
  const text = await res.text();
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : ({} as Record<string, unknown>);

  if (!res.ok) {
    throw new ApiError(
      typeof data['error'] === 'string' ? data['error'] : `http_${res.status}`,
      res.status,
      typeof data['detail'] === 'string' ? data['detail'] : undefined,
      typeof data['code'] === 'string' ? data['code'] : undefined,
    );
  }
  return data as T;
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body),
};
